import { useState, useEffect } from 'react';
import { Palette, Sparkles, Users, Clock, Award } from 'lucide-react';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import MainLayout from '../../components/MainLayout/MainLayout';
import ContentCard from '../../components/Common/ContentCard';
import SecureImage from '../../components/Media/SecureImage';
import mediaService from '../../services/mediaService';
import type { UnifiedContentItem } from '../../types/unifiedContent';
import useSEO from '../../hooks/useSEO';
import { useMediaDuration, formatDuration } from '../../hooks/useMediaDuration';

const AnimationsPage = () => {
  // Apply SEO for animations page
  useSEO();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states  
  const [allContent, setAllContent] = useState<UnifiedContentItem[]>([]);
  const [stats, setStats] = useState({ total: 0, totalViews: 0, standalone: 0, series: 0 });

  // Fetch unified content on component mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the unified animations endpoint that includes both standalone and series content
        const content = await mediaService.getAnimationsCombined();
        
        // AnimationsPage - content fetched and categorized
        
        setAllContent(content);
        
        // Calculate comprehensive stats
        const totalViews = content.reduce((sum, item) => sum + (item.viewCount || 0), 0);
        const standaloneCount = content.filter(item => item.type === 'CONTENT').length;
        const seriesCount = content.filter(item => item.type === 'SERIES').length;
        
        setStats({
          total: content.length,
          totalViews: totalViews,
          standalone: standaloneCount,
          series: seriesCount
        });
      } catch (err) {
        console.error('Error fetching animations:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('Authentication') || errorMessage.includes('credentials') || errorMessage.includes('session')) {
          setError('Unable to load animations. Please log in to access this content.');
        } else if (errorMessage.includes('Network') || errorMessage.includes('connection')) {
          setError('Unable to load animations. Please check your internet connection and refresh the page.');
        } else {
          setError(errorMessage || 'Unable to load animations right now. Please refresh the page or try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Group animations for carousel display
  const getGroupedContent = () => {
    if (allContent.length === 0) {
      return {};
    }

    const grouped: {[key: string]: UnifiedContentItem[]} = {};

    // Featured items (high likes or premium content)
    const potentialFeatured = allContent
      .filter(item => (item.likeCount || 0) > 50 || item.isPremium)
      .sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))
      .slice(0, 12);

    // Trending items (high view count) - use ID-based exclusion
    const featuredIds = new Set(potentialFeatured.map(item => item.id));
    const potentialTrending = allContent
      .filter(item => !featuredIds.has(item.id))
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, 12);

    // Apply minimum threshold (5 items) - if not enough, move to See More
    const minThreshold = 5;
    const featured = potentialFeatured.length >= minThreshold ? potentialFeatured : [];
    const trending = potentialTrending.length >= minThreshold ? potentialTrending : [];
    
    if (featured.length > 0) {
      grouped.featured = featured;
    }
    if (trending.length > 0) {
      grouped.trending = trending;
    }

    // See More: remaining items + items that didn't meet threshold (avoiding duplicates)
    const usedIds = new Set([...featured.map(item => item.id), ...trending.map(item => item.id)]);
    const remainingItems = allContent.filter(item => !usedIds.has(item.id));
    
    // Only add threshold items that aren't already in remainingItems
    const thresholdItems = [
      ...(potentialFeatured.length < minThreshold ? potentialFeatured : []),
      ...(potentialTrending.length < minThreshold ? potentialTrending : [])
    ];
    
    // Use Set to deduplicate by ID
    const seeMoreSet = new Map();
    [...thresholdItems, ...remainingItems].forEach(item => {
      seeMoreSet.set(item.id, item);
    });
    const seeMoreItems = Array.from(seeMoreSet.values()).slice(0, 20);
    
    if (seeMoreItems.length > 0) {
      grouped.seeMore = seeMoreItems;
    }

    return grouped;
  };

  const groupedContent = getGroupedContent();
  const featuredItem = allContent.find(item => item.isPremium || (item.likeCount || 0) > 100) || allContent[0];
  const heroDuration = useMediaDuration(featuredItem?.mediaUrl, featuredItem?.durationSeconds);

  // Helper function to convert UnifiedContentItem to ContentCard props
  const convertToAnimationItem = (item: UnifiedContentItem) => {
    // Generate sample tags based on content type and category
    const generateTags = () => {
      const tags = [];
      
      // Add category as a tag if available
      if (item.category) {
        tags.push(item.category);
      }
      
      // Add animation-specific tags
      tags.push('Animation', 'Visual');
      
      // Add series-specific tag if it's a series
      if (item.type === 'SERIES') {
        tags.push('Series');
      }
      
      // Add featured tag if it's featured
      if (item.isFeatured) {
        tags.push('Featured');
      }
      
      return tags.slice(0, 4); // Limit to 4 tags
    };

    // Determine the correct type based on whether this is a series or content
    const cardType = item.type === 'SERIES' ? 'series' as const : 'animation' as const;

    return {
      id: item.id,
      title: item.title,
      thumbnail: item.thumbnailUrl || '',
      type: cardType,
      tags: generateTags(),
      category: item.category,
      // Add series-related props if it's a series
      ...(item.type === 'SERIES' && {
        totalSeasons: item.totalSeasons,
        totalEpisodes: item.totalEpisodes,
        seriesType: item.contentType as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES',
      }),
    };
  };

  // Format view count for display
  const formatViewCount = (views: number): string => {
    if (views >= 1000000) {
      const millions = views / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    } else if (views >= 1000) {
      const thousands = views / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    return views.toString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">Loading animations...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Palette className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Animations</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <Palette className="h-8 w-8 text-orange-400 mr-3" />
                  <span className="text-orange-400 font-semibold">StrymHub Animations</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    Motion & Magic
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Dive into a world of creative animations. From 2D character studies to complex 3D simulations, 
                  explore the artistry of motion graphics and visual storytelling.
                </p>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                      {stats.total}+
                    </div>
                    <div className="text-sm text-gray-400">Animations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                      {stats.totalViews >= 1000 ? `${formatViewCount(stats.totalViews)}+` : `${stats.totalViews}+`}
                    </div>
                    <div className="text-sm text-gray-400">Total Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">
                      2
                    </div>
                    <div className="text-sm text-gray-400">Content Types</div>
                  </div>
                </div>

                {/* Animation Elements */}
                <div className="flex items-center space-x-4 mb-8">
                  <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
                  <div className="flex space-x-2">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="w-3 h-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </div>
                  <Sparkles className="w-6 h-6 text-cyan-400 animate-pulse" />
                </div>
              </div>
              
              {/* Featured Animation */}
              {featuredItem && (
                <div className="relative">
                  <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <SecureImage 
                        src={featuredItem.thumbnailUrl?.trim() ?
                             featuredItem.thumbnailUrl :
                             `https://picsum.photos/400/600?random=${featuredItem.id || 'animation'}`
                        } 
                        alt={featuredItem.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        Animation
                      </div>
                      
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        {featuredItem.category && (
                          <div className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                            {featuredItem.category}
                          </div>
                        )}
                        <h3 className="text-2xl font-bold mb-2">{featuredItem.title}</h3>
                        <p className="text-gray-200 text-sm mb-4 line-clamp-2">{featuredItem.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {heroDuration ? formatDuration(heroDuration) : featuredItem.type === 'SERIES' ? `${featuredItem.totalEpisodes || 0} episodes` : '0:00'}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {formatViewCount(featuredItem.viewCount || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Animations Collection */}
        <div className="py-8">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse All Animations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover stunning animations and animation series across different styles and complexity levels. 
              Explore both standalone animations and animated series collections.
            </p>
          </div>

        {/* Content Section */}
        <div className="py-8">
          {/* Carousels by Content Type */}
          {Object.entries(groupedContent).map(([type, items]) => {
            if (items.length === 0) return null;
            
            const typeLabels: {[key: string]: string} = {
              featured: 'Featured Animations',
              trending: 'Trending Animations',
              seeMore: 'See More',
            };

            return (
              <div key={type} className="mb-6">
                <Carousel title={typeLabels[type]} id={`animation-${type}`}>
                  {items.map((item) => {
                    const animationItem = convertToAnimationItem(item);
                    return (
                      <CarouselItem key={item.id}>
                        <ContentCard {...animationItem} />
                      </CarouselItem>
                    );
                  })}
                </Carousel>
              </div>
            );
          })}

          {/* Empty State */}
          {allContent.length === 0 && !loading && (
            <div className="max-w-7xl mx-auto text-center py-12">
              <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No animations found</h3>
              <p className="text-gray-600">
                Check back later for animation content and series.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </MainLayout>
  );
};

export default AnimationsPage;